import LogoutOutlined from "@ant-design/icons/LogoutOutlined"
import ReloadOutlined from "@ant-design/icons/ReloadOutlined"
import classNames from "classnames"
import { ReactElement } from "react"
import { useAsyncCallback } from "react-async-hook"

import viewManager from "@/util/viewManager"

import "./PreviewBanner.less"

async function exitPreview(): Promise<void> {
  try {
    await fetch("/_preview/exit", { credentials: "include" })
  } catch {
    // Best-effort cookie deletion — reload regardless so the user
    // sees the current (possibly still-preview) state.
  }
  window.location.reload()
  // Keep the promise pending so useAsyncCallback never flips loading back
  // to false — the button stays in "Exiting…" state until the page unloads.
  await new Promise<void>(() => {})
}

async function refreshPreview(): Promise<void> {
  try {
    await fetch("/_preview/refresh", { credentials: "include" })
  } catch {
    // Best-effort cache bust — reload regardless so the user at least
    // re-fetches the page HTML with whatever manifest the server has.
  }
  window.location.reload()
  // Keep the promise pending so useAsyncCallback never flips loading back
  // to false — the button stays in "Refreshing…" state until the page unloads.
  await new Promise<void>(() => {})
}

export default function PreviewBanner(): ReactElement | null {
  const previewId = viewManager.get("preview") as string | undefined
  const previewStatus = viewManager.get("previewStatus") as string | undefined
  const previewBranch = viewManager.get("previewBranch")
  const previewCommit = viewManager.get("previewCommit")
  const failed = previewStatus === "failed"
  const asyncExit = useAsyncCallback(exitPreview)
  const asyncRefresh = useAsyncCallback(refreshPreview)
  const busy = asyncExit.loading || asyncRefresh.loading

  if (!previewId) {
    return null
  }

  const GITHUB_REPO = "https://github.com/oneschema/oneschema"

  const label = failed ? (
    "Preview failed to load"
  ) : previewBranch || previewCommit ? (
    <>
      {"Preview: "}
      {previewBranch && (
        <a
          href={`${GITHUB_REPO}/tree/${previewBranch}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {previewBranch}
        </a>
      )}
      {previewBranch && previewCommit && " @ "}
      {previewCommit && (
        <a
          href={`${GITHUB_REPO}/commit/${previewCommit}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {previewCommit}
        </a>
      )}
    </>
  ) : (
    "Preview Mode"
  )

  return (
    <div className={classNames("PreviewBanner", { "PreviewBanner--failed": failed })}>
      <div className="PreviewBanner__label">{label}</div>
      <div className="PreviewBanner__actions">
        <button
          type="button"
          onClick={asyncRefresh.execute}
          disabled={busy}
          className="PreviewBanner__button"
          title="Bust the server-side manifest cache and reload with the latest preview build"
          aria-label={asyncRefresh.loading ? "Refreshing" : "Refresh"}
        >
          <ReloadOutlined spin={asyncRefresh.loading} />
          <span>{asyncRefresh.loading ? "Refreshing…" : "Refresh"}</span>
        </button>
        <button
          type="button"
          onClick={asyncExit.execute}
          disabled={busy}
          className="PreviewBanner__button"
          aria-label={asyncExit.loading ? "Exiting preview" : "Exit preview"}
        >
          <LogoutOutlined />
          <span>{asyncExit.loading ? "Exiting…" : "Exit"}</span>
        </button>
      </div>
    </div>
  )
}
